import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  NgZone,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, Validators } from "@angular/forms";
import { take } from "rxjs";
import { htmlToPlainText } from "../../global-functions/global-functions";

@Component({
  selector: "app-love-it-select",
  templateUrl: "./love-it-select.component.html",
  styleUrls: ["./love-it-select.component.scss"],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LoveItSelectComponent),
      multi: true,
    },
  ],
})
export class LoveItSelectComponent implements ControlValueAccessor, OnChanges {
  @Input() formControl: FormControl;
  @Input() submitted: boolean;
  @Input() enableAddTag: boolean;
  @Input() label: string;
  @Input() hideLabel: boolean;
  @Input() items: Array<{ value: string; label: string }> = [];
  @Input() selectId: string;
  @Input() placeholder: string;
  @Input() notFoundText: string;
  @Input() addTagText: string;
  @Input() disabled: boolean;
  @Input() clearable: boolean = true;
  @Input() autoSortAlphabetically: boolean = true;
  @Input() value: string = "";
  @Input() wrapperNgClass: string | {} = "";

  @Output() selectBlur: EventEmitter<string> = new EventEmitter<string>();
  @Output() selectFocus: EventEmitter<string> = new EventEmitter<string>();
  @Output() selectClear: EventEmitter<string> = new EventEmitter<string>();
  @Output() selectChange: EventEmitter<{ value: string; label: string }> = new EventEmitter<{
    value: string;
    label: string;
  }>();
  @Output() selectClose: EventEmitter<string> = new EventEmitter<string>();
  @Output() selectOpen: EventEmitter<string> = new EventEmitter<string>();
  @Output() selectItemsAdded: EventEmitter<Array<{ value: string; label: string }>> = new EventEmitter<
    Array<{ value: string; label: string }>
  >();

  @ViewChild("ngSelect", { read: ElementRef }) ngSelectEl!: ElementRef<HTMLElement>;

  onChange: (value: string) => void = () => {};
  onTouched: () => void = () => {};

  ngOnChanges(changes: SimpleChanges) {
    if (changes["items"]) {
      this.sanitizeItems();
      if (!changes["items"].firstChange) {
        this.onItemsRefreshed();
      }
    }
    if (changes["disabled"] && this.formControl) {
      if (this.disabled) {
        this.formControl.disable({ emitEvent: false });
      } else {
        this.formControl.enable({ emitEvent: false });
      }
    }
  }

  private clearQueued = false;

  constructor(private zone: NgZone) {}

  writeValue(value: string): void {
    this.value = value ?? "";
    this.queueClearSelectInnerHtml(this.value || null);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  protected onSelectBlur(event) {
    const value = event?.target?.value ?? this.formControl.value ?? "";
    if (!value) {
      return;
    }

    this.addOptionIfNew(value);

    this.selectBlur.emit(event);
    this.onTouched();

    this.queueClearSelectInnerHtml(value);
  }

  protected onSelectClose(event) {
    const value = event?.target?.value ?? this.formControl.value ?? "";
    this.addOptionIfNew(value);

    this.selectClose.emit(event);
    this.queueClearSelectInnerHtml(value);
  }

  protected onSelectOpen(event) {
    const value = event?.target?.value ?? this.formControl.value ?? "";

    this.sortItems();
    this.onTouched();
    this.selectOpen.emit(event);
    this.queueClearSelectInnerHtml(value);
  }

  protected onSelectFocus(event) {
    const input = event.target;
    input.value = this.findLabelForValue(this.formControl.value ?? "");

    this.selectFocus.emit(event);
    this.queueClearSelectInnerHtml(input?.value);
  }

  protected onSelectClear(event) {
    this.formControl.setValue(null);
    this.selectClear.emit(event);
    this.queueClearSelectInnerHtml();
  }

  protected onSelectChange(event) {
    this.formControl.markAsDirty();
    this.formControl.setValue(event?.value ?? "", { emitEvent: true });
    if (event?.value) {
      event.value = this.formControl.value ?? "";
    }

    this.selectChange.emit(event);
    this.onChange(this.formControl.value ?? "");

    this.queueClearSelectInnerHtml(event?.value);
  }

  protected createNewTag(name: string) {
    const trimmedName = name.trim();
    return { label: trimmedName, value: trimmedName };
  }

  private queueClearSelectInnerHtml(inputValue: string = null) {
    if (this.clearQueued) {
      return;
    }
    this.clearQueued = true;

    this.zone.onStable.pipe(take(1)).subscribe(() => {
      this.clearQueued = false;
      this.clearSelectInnerHtml(inputValue);
    });
  }

  private clearSelectInnerHtml(inputValue: string = null) {
    const nativeEl = this.ngSelectEl?.nativeElement;
    nativeEl.querySelector(".ng-value.ng-value-label")?.remove();
    const label = this.findLabelForValue(inputValue);

    if (nativeEl?.querySelector(".ng-value")) {
      nativeEl.querySelector(".ng-value").innerHTML =
        `<span style="visibility: hidden;">${htmlToPlainText(label)}</span>`;
    }
    nativeEl.querySelector(".ng-value.ng-star-inserted")?.remove();

    const inputEl = nativeEl.querySelector(".ng-input input") as HTMLInputElement | null;
    if (inputValue) {
      if (inputEl) {
        inputEl.value = htmlToPlainText(label);
      }
    } else {
      if (inputEl) {
        inputEl.value = "";
      }
    }
  }

  private sanitizeItems() {
    this.items = (this.items ?? []).map((item) => ({
      ...item,
      label: htmlToPlainText(item.label),
    }));
  }

  private sortItems() {
    if (!this.autoSortAlphabetically) {
      return;
    }
    const items = this.items ?? [];
    this.items = [...items].sort((a, b) => (a.label ?? "").localeCompare(b.label ?? ""));
  }

  private addOptionIfNew(value: string) {
    if (this.enableAddTag !== true) {
      return;
    }
    const existing = this.items?.find((opt) => opt.label.toLowerCase() === value.toLowerCase());
    if (!existing) {
      const option = existing ?? this.createNewTag(value);
      this.formControl.markAsDirty();
      this.formControl.setValue(option.value, { emitEvent: true });
      this.items = [...(this.items ?? []), option];
      this.sortItems();
      this.onChange(option.value);
      this.selectItemsAdded.emit(this.items);
    }
  }

  private findLabelForValue(value: string) {
    return this.items?.find((opt) => opt.value === value)?.label ?? value;
  }

  private onItemsRefreshed() {
    this.queueClearSelectInnerHtml(this.formControl?.value ?? null);
  }

  protected readonly Validators = Validators;
}
